import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Clock, Check, Glasses, Eye } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { it as dfIt, de as dfDe, fr as dfFr, enUS } from "date-fns/locale";

const DF_LOCALES = { it: dfIt, en: enUS, de: dfDe, fr: dfFr };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES_BY_AMBITO = {
  vista: ["svc_vista", "svc_occhiali", "svc_sole", "svc_progressive", "svc_bambini", "svc_altro"],
  lenti: ["svc_lenti_prima", "svc_lenti_controllo", "svc_altro"],
};
const AMBITI = ["vista", "lenti"];
const MORNING = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30"];
const AFTERNOON = ["17:30", "18:00", "18:30", "19:00", "19:30"];
const EMPTY = { nome: "", telefono: "", ambito: "", servizio: "", data: "", orario: "", note: "" };

const slotsFor = (weekday) => {
  if (weekday === null || weekday === 0) return [];
  if (weekday === 1) return AFTERNOON;
  return [...MORNING, ...AFTERNOON];
};

export default function Prenota({ compact = false, initialAmbito = null }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [taken, setTaken] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialAmbito === "vista" || initialAmbito === "lenti") {
      setForm((f) => ({ ...f, ambito: initialAmbito, servizio: "" }));
    }
  }, [initialAmbito]);

  const minDay = new Date();
  minDay.setDate(minDay.getDate() + 2);
  minDay.setHours(0, 0, 0, 0);
  const weekday = form.data ? new Date(`${form.data}T12:00:00`).getDay() : null;
  const isSunday = weekday === 0;
  const isMonday = weekday === 1;
  const slots = slotsFor(weekday);

  useEffect(() => {
    if (!form.data || isSunday) {
      setTaken([]);
      return;
    }
    axios
      .get(`${API}/appointments/slots`, { params: { data: form.data } })
      .then((r) => setTaken(r.data.occupati || []))
      .catch(() => setTaken([]));
  }, [form.data]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (isSunday) {
      toast.error(t("book_sunday"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      const repartoLabel = form.ambito === "lenti" ? t("ambito_lenti") : t("ambito_vista");
      const waMsg = [
        "Ciao Stylottica Ceglie! Vorrei prenotare una visita.",
        `Nome: ${form.nome}`,
        `Telefono: ${form.telefono}`,
        `Reparto: ${repartoLabel}`,
        `Servizio: ${form.servizio}`,
        `Giorno: ${form.data}`,
        `Orario: ${form.orario}`,
        form.note ? `Note: ${form.note}` : null,
      ]
        .filter(Boolean)
        .join("\n");
      const waUrl = `https://wa.me/393925218114?text=${encodeURIComponent(waMsg)}`;
      window.open(waUrl, "_blank");
      toast.success(t("book_toast_ok"), {
        duration: 8000,
        action: { label: "WhatsApp", onClick: () => window.open(waUrl, "_blank") },
      });
      setForm(EMPTY);
      setConsent(false);
      setTaken([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("book_toast_err"));
      if (form.data) {
        axios
          .get(`${API}/appointments/slots`, { params: { data: form.data } })
          .then((r) => setTaken(r.data.occupati || []))
          .catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  };

  const slotCls = (active, disabled) =>
    `rounded-full border text-xs tracking-[0.08em] px-3 py-2.5 transition-colors duration-300 ${
      disabled
        ? "border-[#DCD7CC] text-[#1A1B18]/30 line-through cursor-not-allowed"
        : active
          ? "bg-[#1A1B18] text-white border-[#1A1B18]"
          : "border-[#DCD7CC] text-[#1A1B18]/70 hover:border-[#1A1B18]"
    }`;

  const label = "block text-[10px] uppercase tracking-[0.2em] text-[#1A1B18]/50 mb-2";

  return (
    <section id="prenota" data-testid="prenota-section" className={(compact ? "py-16 md:py-24" : "py-24 md:py-32") + " border-t border-[#1A1B18]/10"}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#A86946] mb-4">{t("book_overline")}</p>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter leading-[1.05]">
            {t("book_title")}
          </h2>
          <p className="mt-6 text-base text-[#1A1B18]/60 max-w-md leading-relaxed">{t("book_sub")}</p>
          <div className="mt-10 max-w-md text-sm text-[#1A1B18]/60">
            <p className="flex justify-between gap-4 border-t border-[#1A1B18]/10 py-3">
              <span className="font-display text-lg font-medium text-[#1A1B18]">{t("day_tuesat")}</span>
              <span className="text-right">09:00 – 12:45 · 17:30 – 20:00</span>
            </p>
            <p className="flex justify-between gap-4 py-3">
              <span className="font-display text-lg font-medium text-[#1A1B18]">{t("day_mon")}</span>
              <span className="text-right">17:30 – 20:00 · {t("mon_note")}</span>
            </p>
            <p className="flex justify-between gap-4 border-b border-[#1A1B18]/10 py-3">
              <span className="font-display text-lg font-medium text-[#1A1B18]">{t("day_sun")}</span>
              <span className="text-right">{t("closed")}</span>
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <form onSubmit={submit} data-testid="book-form" className="space-y-6">
            <div>
              <span className={`${label} !text-sm !text-[#1A1B18] !normal-case !tracking-normal font-medium`}>
                {t("book_ambito")} *
              </span>
              <div data-testid="book-ambito" className="grid grid-cols-2 gap-3 md:gap-4">
                {AMBITI.map((a) => {
                  const selected = form.ambito === a;
                  const Icon = a === "lenti" ? Eye : Glasses;
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setForm({ ...form, ambito: a, servizio: "" })}
                      data-testid={`book-ambito-${a}`}
                      className={`group relative overflow-hidden rounded-2xl border-2 text-left transition-all duration-300 ${
                        selected
                          ? "border-[#A86946] ring-2 ring-[#A86946]/30 shadow-lg shadow-[#A86946]/10"
                          : "border-[#DCD7CC] hover:border-[#A86946]/60"
                      }`}
                    >
                      <div className="relative h-24 sm:h-32 overflow-hidden">
                        <img
                          src={a === "lenti" ? "/images/reparto-lenti.jpg" : "/images/reparto-vista.jpg"}
                          alt=""
                          loading="lazy"
                          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${selected ? "scale-105" : ""}`}
                        />
                        <div className={`absolute inset-0 transition-colors duration-300 ${selected ? "bg-[#A86946]/35" : "bg-[#1A1B18]/25 group-hover:bg-[#1A1B18]/15"}`} />
                        {selected && (
                          <span className="absolute top-2 right-2 flex items-center justify-center w-6 h-6 rounded-full bg-[#A86946] text-white">
                            <Check size={14} strokeWidth={3} />
                          </span>
                        )}
                      </div>
                      <div className={`px-3 py-3 transition-colors duration-300 ${selected ? "bg-[#A86946] text-white" : "bg-white text-[#1A1B18]"}`}>
                        <span className="flex items-center gap-2">
                          <Icon size={16} strokeWidth={1.75} className={selected ? "text-white" : "text-[#A86946]"} />
                          <span className="font-semibold text-sm sm:text-base leading-tight">
                            {a === "lenti" ? t("ambito_lenti") : t("ambito_vista")}
                          </span>
                        </span>
                        <span className={`block text-[10px] sm:text-[11px] mt-1 ${selected ? "text-white/80" : "text-[#1A1B18]/50"}`}>
                          {a === "lenti" ? t("ambito_lenti_sub") : t("ambito_vista_sub")}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <span className={label}>{t("ph_name")} *</span>
              <Input
                required
                value={form.nome}
                onChange={set("nome")}
                placeholder={t("ph_name")}
                data-testid="book-name"
                className="rounded-xl border-[#DCD7CC] bg-transparent h-12"
              />
            </div>
            <div>
              <span className={label}>{t("book_phone")} *</span>
              <Input
                required
                type="tel"
                value={form.telefono}
                onChange={set("telefono")}
                placeholder={t("book_phone")}
                data-testid="book-phone"
                className="rounded-xl border-[#DCD7CC] bg-transparent h-12"
              />
            </div>
            <div>
              <span className={label}>{t("book_service")} *</span>
              <select
                required
                value={form.servizio}
                onChange={set("servizio")}
                disabled={!form.ambito}
                data-testid="book-service"
                className="w-full rounded-xl border border-[#DCD7CC] bg-transparent h-12 px-3 text-sm focus:outline-none focus:border-[#A86946] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="" disabled>
                  {form.ambito ? t("book_service_ph") : t("book_ambito_first")}
                </option>
                {(SERVICES_BY_AMBITO[form.ambito] || []).map((s) => (
                  <option key={s} value={t(s)}>
                    {t(s)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className={label}>{t("book_date")} *</span>
              <div data-testid="book-calendar" className="rounded-xl border border-[#DCD7CC] inline-block bg-transparent">
                <Calendar
                  mode="single"
                  locale={DF_LOCALES[lang] || dfIt}
                  selected={form.data ? new Date(`${form.data}T12:00:00`) : undefined}
                  onSelect={(d) =>
                    setForm({ ...form, data: d ? d.toLocaleDateString("en-CA") : "", orario: "" })
                  }
                  disabled={(d) => d < minDay || d.getDay() === 0}
                  defaultMonth={minDay}
                />
              </div>
            </div>
            {form.data && !isSunday && (
              <div>
                <span className={label}>
                  {t("book_slot")} *
                </span>
                <div data-testid="book-slots" className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slots.map((s) => (
                    <button
                      key={s}
                      type="button"
                      disabled={taken.includes(s)}
                      onClick={() => setForm({ ...form, orario: s })}
                      data-testid={`book-slot-${s.replace(":", "")}`}
                      className={slotCls(form.orario === s, taken.includes(s))}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {isMonday && (
                  <p data-testid="book-monday-note" className="mt-2 text-xs text-[#A86946]">
                    {t("book_monday")}
                  </p>
                )}
              </div>
            )}
            {isSunday && (
              <p data-testid="book-sunday-note" className="text-xs text-red-700">
                {t("book_sunday")}
              </p>
            )}
            <textarea
              value={form.note}
              onChange={set("note")}
              placeholder={t("cart_ph_note")}
              data-testid="book-note"
              className="w-full rounded-xl border border-[#DCD7CC] bg-transparent p-3 text-sm min-h-[90px] focus:outline-none focus:border-[#A86946]"
            />
            <label data-testid="book-consent" className="flex items-start gap-3 text-[12px] text-[#1A1B18]/60 leading-relaxed cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="book-consent-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#A86946] shrink-0"
              />
              <span>
                {t("book_consent")}{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#A86946] underline underline-offset-2 hover:text-[#1A1B18]">
                  {t("privacy_link")}
                </a>
                .
              </span>
            </label>
            <button
              type="submit"
              disabled={loading || isSunday || !form.ambito || !form.orario || !consent}
              data-testid="book-submit"
              className="flex items-center gap-2 rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-7 py-3.5 hover:bg-[#1A1B18] transition-colors duration-300 disabled:opacity-40"
            >
              {loading ? <Clock size={15} /> : <CalendarCheck size={15} />}
              {loading ? t("book_sending") : t("book_submit")}
            </button>
            <p className="text-[11px] text-[#1A1B18]/40">
              {t("privacy_note")}{" "}
              <a href="/privacy" className="text-[#A86946] underline underline-offset-2 hover:text-[#1A1B18]">
                {t("privacy_link")}
              </a>
              .
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
